import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import InputGroupV2 from '../InputGroupV2';
import BaseService from 'services/BaseService';

const filterUsuarioOptions = (options, { inputValue }) => {
  if (!inputValue) {
    return options;
  }
  const q = inputValue.toUpperCase();
  return options.filter(
    (u) =>
      (u.nome && u.nome.toUpperCase().includes(q)) ||
      (u.matricula && String(u.matricula).toUpperCase().includes(q))
  );
};

const UsuarioSelectV2 = ({
  onSelect,
  value,
  isRequired,
  error,
  grupos,
  label,
  disabled,
  ativo,
  showMatriculaCemig,
  multiSelect,
}) => {
  const [usuarios, setUsuarios] = useState([]);

  const getUsuarios = async () => {
    const params = new URLSearchParams();
    if (grupos) params.append('roles', grupos);
    if (ativo) params.append('ativo', ativo);
    const queryString = params.toString();
    const url = queryString ? `usuarios/roles?${queryString}` : 'usuarios/roles';
    try {
      const response = await BaseService.get({ url });
      setUsuarios(response?.data ?? []);
    } catch (e) {
      console.error('Erro ao buscar usuários:', e);
    }
  };

  useEffect(() => {
    getUsuarios();
  }, []);

  const getSingleValue = () => {
    if (value && usuarios && usuarios.length > 0) {
      const found = usuarios.filter((i) => i.id === value);
      if (found.length > 0) {
        return found[0];
      }
    }
    return null;
  };

  const multiValue = useMemo(() => {
    if (!multiSelect || !value || !usuarios.length) {
      return [];
    }
    return usuarios.filter((u) => value.includes(u.id));
  }, [multiSelect, value, usuarios]);

  const singleForMatricula = getSingleValue();
  const showMatriculaFooter =
    !multiSelect &&
    showMatriculaCemig &&
    singleForMatricula != null &&
    singleForMatricula['matriculaCemig'];

  return (
    <InputGroupV2
      label={label}
      isRequired={isRequired}
    >
      <Autocomplete
        disabled={disabled}
        disableClearable={!multiSelect}
        filterOptions={filterUsuarioOptions}
        getOptionLabel={(option) => (option && option.nome ? option.nome : '')}
        isOptionEqualToValue={(a, b) => a && b && a.id === b.id}
        multiple={multiSelect}
        noOptionsText="Sem opções"
        options={usuarios}
        value={multiSelect ? multiValue : getSingleValue()}
        onChange={(event, newValue) => {
          if (multiSelect) {
            onSelect(newValue && newValue.length ? newValue.map((u) => u.id) : []);
          } else if (newValue) {
            onSelect(newValue.id, newValue.nome);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            fullWidth
            margin="dense"
            placeholder="Selecione..."
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: disabled ? 'transparent' : 'background.paper',
                minHeight: 38,
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Box
              sx={{
                bgcolor: selected ? 'primary.main' : 'transparent',
                color: selected ? 'primary.contrastText' : 'text.primary',
                cursor: 'default',
                display: 'block',
                fontSize: 'inherit',
                py: 1,
                px: 1.5,
                userSelect: 'none',
                width: '100%',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: selected ? 'primary.contrastText' : 'text.primary' }}
              >
                {option.nome} - {option.matricula != null ? option.matricula : ''}
              </Typography>
              <Typography
                variant="caption"
                component="div"
              >
                <Box
                  component="span"
                  sx={{ fontWeight: 'bold' }}
                >
                  Status:{' '}
                </Box>
                {option.ativo === true ? 'Ativo' : 'Inativo'}
              </Typography>
            </Box>
          </li>
        )}
        slotProps={{
          popper: {
            sx: (theme) => ({
              zIndex: theme.zIndex.modal + 1,
            }),
          },
        }}
      />
      {error && (
        <Typography
          variant="caption"
          sx={{ color: 'error.main', mt: 1, ml: 1.5, display: 'block' }}
        >
          O campo é obrigatório
        </Typography>
      )}
      {showMatriculaFooter ? (
        <Typography
          variant="caption"
          sx={{ color: 'primary.main', mt: 1, ml: 1.5, display: 'block' }}
        >
          {`Matrícula Cemig: ${singleForMatricula['matriculaCemig']}`}
        </Typography>
      ) : null}
    </InputGroupV2>
  );
};

UsuarioSelectV2.propTypes = {
  error: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isRequired: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
  grupos: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  label: PropTypes.string,
  disabled: PropTypes.bool,
  ativo: PropTypes.bool,
  multiSelect: PropTypes.bool,
  showMatriculaCemig: PropTypes.bool,
};

UsuarioSelectV2.defaultProps = {
  error: false,
  isRequired: false,
  grupos: null,
  label: 'Usuário',
  disabled: false,
  ativo: null,
  multiSelect: false,
  showMatriculaCemig: false,
};

export default UsuarioSelectV2;
